"use client";

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  quote: string;
  name: string;
  role: string;
  image: string;
  imageAlt: string;
  story: string;
  rating: number;
}

const TestimonialsSection = () => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const testimonials: Testimonial[] = [
  {
    id: 'testimonial_sarah',
    quote: "Seeing my mom's face and hearing her voice on my 18th birthday was the most precious gift I've ever received.",
    name: 'Emma Rodriguez',
    role: '18 years old',
    image: "/images/testimonial-old-person-1.png",
    imageAlt: 'Young woman with long dark hair smiling warmly at camera in natural outdoor setting',
    story: 'Sarah sent her daughter Emma a video message to be delivered on her 18th birthday, 3 years after her passing.',
    rating: 5
  },
  {
    id: 'testimonial_michael',
    quote: "My grandfather's letters taught me lessons I needed exactly when I needed them. It's like he's still guiding me.",
    name: 'Michael Chen',
    role: 'College Graduate',
    image: "/images/testimonial-old-1.jpg",
    imageAlt: 'Young man with short black hair in casual blue shirt smiling confidently',
    story: 'His grandfather wrote 20 letters to be delivered at major life milestones - graduation, first job, marriage.',
    rating: 5
  },
  {
    id: 'testimonial_lisa',
    quote: "The memorial page we created for Dad has become a place where our whole family connects and shares memories.",
    name: 'Lisa Thompson',
    role: 'Family Organizer',
    image: "/images/testimonial-old-person-2.png",
    imageAlt: 'Middle-aged woman with blonde hair smiling warmly in professional setting',
    story: 'Lisa created a memorial page with photos, stories, and her father\'s favorite music for the entire family.',
    rating: 5
  },
  {
    id: 'testimonial_james',
    quote: "Knowing my messages are scheduled and secure lets me focus on living, not worrying about what's left unsaid.",
    name: 'James Wilson',
    role: 'Father of 4',
    image: "/images/testimonial-old-2.jpg",
    imageAlt: 'Man with beard wearing casual shirt smiling warmly in outdoor setting with soft lighting',
    story: 'James has created messages for each child\'s wedding day, graduation, and other major life events.',
    rating: 5
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleCards((prev) => prev.includes(index) ? prev : [...prev, index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cardElements = sectionRef.current?.querySelectorAll('.testimonial-card');
    cardElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-2 bg-success/10 text-success rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
            Real Stories
          </span>
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Stories That Touch Hearts
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Hear from families who have experienced the power of preserved memories and final words.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) =>
          <div
            key={testimonial.id}
            data-index={index}
            className={`testimonial-card bg-card rounded-3xl border border-border p-8 hover:shadow-2xl transition-all duration-500 ${
            visibleCards.includes(index) ?
            'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`
            }
            style={{ transitionDelay: `${index * 150}ms` }}>
            
              {/* Rating Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonial.rating)].map((_, i) =>
              <Icon key={`star_${testimonial.id}_${i}`} name="StarIcon" size={20} className="text-amber-500" variant="solid" />
              )}
              </div>

              {/* Quote */}
              <blockquote className="font-crimson text-xl italic text-foreground leading-relaxed mb-6">
                &quot;{testimonial.quote}&quot;
              </blockquote>

              {/* Story Context */}
              <p className="text-muted-foreground text-sm leading-relaxed mb-6 border-l-4 border-primary/30 pl-4">
                {testimonial.story}
              </p>

              {/* Author */}
              <div className="flex items-center gap-4 pt-6 border-t border-border">
                <AppImage
                src={testimonial.image}
                alt={testimonial.imageAlt}
                className="w-14 h-14 rounded-full object-cover" />
              
                <div>
                  <p className="font-jakarta font-bold text-foreground">{testimonial.name}</p>
                  <p className="text-muted-foreground text-sm">{testimonial.role}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;